import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, BadgeCheck, Search, ChevronDown, User, Video, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/context/ThemeContext";
import SearchOverlay from "@/components/SearchOverlay";
import { ADMIN_PORTAL } from "@/config";

const LINKS = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Live Creators", to: "/creators" },
  { label: "Partners", to: "/partners" },
  { label: "Blog", to: "/blog" },
];

const LOGIN_OPTIONS = [
  { to: "/login", label: "User login", hint: "Viewers & applicants", Icon: User, testid: "login-option-user" },
  { to: "/creator/login", label: "Creator login", hint: "Approved creators", Icon: Video, testid: "login-option-creator" },
  { to: `${ADMIN_PORTAL}/login`, label: "Team login", hint: "Destiny Tip staff only", Icon: ShieldCheck, testid: "login-option-team" },
];

function LoginChooser({ className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-sm text-dt-muted hover:text-dt-text transition-colors"
        data-testid="nav-login-link" aria-expanded={open} aria-haspopup="menu">
        Log In <ChevronDown className={`w-3.5 h-3.5 toggle-spring ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="dropdown-ease absolute right-0 top-9 w-64 card-dt glass-panel p-2 z-50" role="menu" data-testid="login-chooser">
          <p className="text-[10px] uppercase tracking-[0.18em] text-dt-muted font-bold px-3 pt-2 pb-1.5">Choose your portal</p>
          {LOGIN_OPTIONS.map((o) => (
            <Link key={o.to} to={o.to} role="menuitem"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-dt-accent/10 transition-colors group"
              data-testid={o.testid}>
              <span className="w-8 h-8 rounded-lg border border-dt-border bg-dt-bg2 flex items-center justify-center text-dt-muted group-hover:text-dt-accent group-hover:border-dt-accent/50 transition-colors">
                <o.Icon className="w-4 h-4" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-dt-text">{o.label}</span>
                <span className="block text-[11px] text-dt-muted">{o.hint}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const accountLink = isAdmin
    ? { to: ADMIN_PORTAL, label: "Dashboard", testid: "nav-admin-link" }
    : user?.role === "creator"
      ? { to: "/creator/dashboard", label: "Dashboard", testid: "nav-dashboard-link" }
      : user
        ? { to: "/account", label: "My Account", testid: "nav-account-link" }
        : { to: "/login", label: "Log In", testid: "nav-login-link" };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || open ? "glass-nav" : "bg-transparent border-b border-transparent"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo" aria-label="Destiny Tip — home">
          <img src="/assets/logo.png" alt="Destiny Tip logo" className="w-9 h-9 rounded-full object-cover ring-1 ring-dt-border" />
          <span className="font-heading font-bold tracking-[0.18em] text-sm sm:text-base text-dt-text">DESTINY TIP</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {LINKS.map((l) => (
            <Link key={l.label} to={l.to}
              className={`text-sm transition-colors duration-200 ${location.pathname === l.to ? "text-dt-text font-semibold" : "text-dt-muted hover:text-dt-text"}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => setSearchOpen(true)} aria-label="Search the site"
            className="w-9 h-9 rounded-full border border-dt-border bg-dt-card flex items-center justify-center text-dt-muted hover:text-dt-text hover:border-dt-accent transition-all hover:scale-110"
            data-testid="nav-search-btn">
            <Search className="w-4 h-4" />
          </button>
          <ThemeToggle />
          {user ? (
            <Link to={accountLink.to} className="text-sm text-dt-muted hover:text-dt-text transition-colors" data-testid={accountLink.testid}>
              {accountLink.label}
            </Link>
          ) : (
            <LoginChooser />
          )}
          <button
            onClick={() => navigate("/apply")}
            className="glow-pulse bg-dt-silver text-dt-bg font-semibold rounded-full px-5 py-2.5 text-sm transition-transform duration-200 hover:scale-105 hover:brightness-110"
            data-testid="apply-creator-btn"
          >
            Apply as a Creator
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button onClick={() => setSearchOpen(true)} aria-label="Search the site"
            className="w-9 h-9 rounded-full border border-dt-border bg-dt-card flex items-center justify-center text-dt-muted"
            data-testid="nav-search-btn-mobile">
            <Search className="w-4 h-4" />
          </button>
          <ThemeToggle />
          <button className="text-dt-text p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-dt-border px-6 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <Link key={l.label} to={l.to} className="text-sm text-dt-muted hover:text-dt-text">{l.label}</Link>
          ))}
          <Link to="/brands" className="text-sm text-dt-muted hover:text-dt-text">For Brands</Link>
          <div className="flex gap-3 pt-2">
            {user ? (
              <Link to={accountLink.to} className="text-sm text-dt-text" data-testid={accountLink.testid}>{accountLink.label}</Link>
            ) : (
              <div className="flex flex-col gap-2.5" data-testid="login-chooser-mobile">
                <p className="text-[10px] uppercase tracking-[0.18em] text-dt-muted font-bold">Log in as</p>
                {LOGIN_OPTIONS.map((o) => (
                  <Link key={o.to} to={o.to} className="flex items-center gap-2.5 text-sm text-dt-text" data-testid={`${o.testid}-mobile`}>
                    <o.Icon className="w-4 h-4 text-dt-accent" /> {o.label}
                    <span className="text-[11px] text-dt-muted">— {o.hint}</span>
                  </Link>
                ))}
              </div>
            )}
            <Link to="/apply" className="bg-dt-silver text-dt-bg font-semibold rounded-full px-4 py-2 text-sm h-fit">Apply as a Creator</Link>
          </div>
        </div>
      )}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}

export function VerifiedBadge({ className = "w-4 h-4" }) {
  return <BadgeCheck className={`${className} text-dt-accent`} aria-label="Verified Destiny Tip Partner" />;
}
